import React, { useEffect, useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { useLocation, Link } from "react-router-dom";

const links = [
    { href: "#services", label: "Services" },
    { href: "#how", label: "How it works" },
    { href: "#gallery", label: "Results" },
    { href: "#about", label: "About" },
    { href: "#contact", label: "Quote" },
];

export default function Nav() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { pathname } = useLocation();
    const isHome = pathname === "/";

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const onDark = isHome && !scrolled;

    const renderLink = (l) => {
        const href = isHome ? l.href : `/${l.href}`;
        return (
            <a
                key={l.href}
                href={href}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`px-4 py-2 text-[13px] font-medium transition-colors relative group ${
                    onDark
                        ? "text-[rgba(242,237,227,0.92)] hover:text-[var(--paper)]"
                        : "text-[rgba(14,14,16,0.80)] hover:text-[var(--ink)]"
                }`}
            >
                {l.label}
                <span
                    className={`absolute left-4 right-4 -bottom-0.5 h-px scale-x-0 group-hover:scale-x-100 transition-transform origin-left ${
                        onDark ? "bg-[var(--paper)]" : "bg-[var(--ink)]"
                    }`}
                />
            </a>
        );
    };

    return (
        <header
            data-testid="top-nav"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                scrolled || !isHome
                    ? "bg-[rgba(242,237,227,0.85)] backdrop-blur-xl border-b border-line"
                    : "bg-transparent"
            }`}
        >
            <div className="maxw section-pad flex items-center justify-between py-4">
                <Link
                    to="/"
                    className="flex items-center gap-3"
                    data-testid="brand-mark"
                >
                    <span
                        className={`relative w-10 h-10 sm:w-11 sm:h-11 rounded-[10px] flex items-center justify-center transition-colors duration-500 overflow-hidden ${
                            onDark
                                ? "bg-[var(--paper)] ring-1 ring-white/10"
                                : "bg-[var(--paper)] ring-1 ring-black/5"
                        }`}
                    >
                        <img
                            src="/logo-mark.png"
                            alt="FreshEdge Services"
                            className="w-[88%] h-[88%] object-contain"
                            width="44"
                            height="44"
                            loading="eager"
                            decoding="async"
                        />
                    </span>
                    <div className="leading-tight">
                        <div
                            className={`font-display text-[16px] font-medium tracking-tight transition-colors duration-500 ${
                                onDark
                                    ? "text-[var(--paper)]"
                                    : "text-[var(--ink)]"
                            }`}
                        >
                            FreshEdge Services
                        </div>
                        <div
                            className={`font-mono-x text-[10px] uppercase tracking-[0.18em] transition-colors duration-500 ${
                                onDark
                                    ? "text-[rgba(242,237,227,0.72)]"
                                    : "text-[rgba(14,14,16,0.58)]"
                            }`}
                        >
                            Lawn · Garden · Outdoor
                        </div>
                    </div>
                </Link>

                <nav className="hidden lg:flex items-center gap-1">
                    {links.map(renderLink)}
                </nav>

                <div className="hidden lg:flex items-center gap-3">
                    <a
                        href="tel:0435995971"
                        data-testid="cta-call"
                        className={`inline-flex items-center gap-2 text-[13px] font-medium transition-colors ${
                            onDark
                                ? "text-[rgba(242,237,227,0.92)] hover:text-[var(--paper)]"
                                : "text-[rgba(14,14,16,0.75)] hover:text-[var(--ink)]"
                        }`}
                    >
                        <Phone size={14} />
                        Call us
                    </a>
                    <a
                        href={isHome ? "#contact" : "/contact"}
                        data-testid="cta-quote"
                        className={`inline-flex items-center gap-2 text-[13px] font-medium py-2.5 px-4 rounded-full border transition-all duration-300 ${
                            onDark
                                ? "bg-[var(--paper)] text-[var(--ink)] border-[var(--paper)] hover:bg-[var(--terra)] hover:border-[var(--terra)] hover:text-[var(--paper)]"
                                : "bg-[var(--ink)] text-[var(--paper)] border-[var(--ink)] hover:bg-[var(--terra)] hover:border-[var(--terra)]"
                        }`}
                    >
                        Get a free quote
                        <span className="text-base leading-none">→</span>
                    </a>
                </div>

                <button
                    onClick={() => setOpen(!open)}
                    className={`lg:hidden p-2 -mr-2 ${
                        onDark ? "text-[var(--paper)]" : "text-[var(--ink)]"
                    }`}
                    data-testid="mobile-menu-toggle"
                    aria-label="Toggle menu"
                >
                    {open ? <X size={22} /> : <Menu size={22} />}
                </button>
            </div>

            {open && (
                <div
                    className="lg:hidden border-t border-line bg-[var(--paper)]"
                    data-testid="mobile-menu"
                >
                    <div className="section-pad py-4 flex flex-col gap-1">
                        {links.map((l) => (
                            <a
                                key={l.href}
                                href={isHome ? l.href : `/${l.href}`}
                                onClick={() => setOpen(false)}
                                className="py-3 text-[15px] font-medium text-[var(--ink)] border-b border-line last:border-0"
                            >
                                {l.label}
                            </a>
                        ))}
                        <a
                            href={isHome ? "#contact" : "/contact"}
                            onClick={() => setOpen(false)}
                            className="btn-ink mt-4 justify-center"
                        >
                            Get a free quote →
                        </a>
                    </div>
                </div>
            )}
        </header>
    );
}
